
import { CheckCircle, XCircle, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { AdminCampaign } from "../types";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

interface CampaignActionButtonsProps {
  campaign: AdminCampaign;
  updating: string | null;
  onUpdateStatus: (campaignId: string, newStatus: 'approved' | 'rejected' | 'pending', rejectionReason?: string) => void;
}

export const CampaignActionButtons = ({ campaign, updating, onUpdateStatus }: CampaignActionButtonsProps) => {
  const { language } = useLanguage();
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");

  const handleReject = () => {
    setShowRejectDialog(true);
  };

  const confirmReject = () => {
    onUpdateStatus(campaign.id, 'rejected', rejectionReason);
    setShowRejectDialog(false);
    setRejectionReason("");
  };

  return (
    <>
      <div className="flex gap-2">
        {campaign.status === 'pending' && (
          <>
            <Button
              onClick={() => onUpdateStatus(campaign.id, 'approved')}
              disabled={updating === campaign.id}
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700"
              title={language === 'ar' ? 'موافقة' : 'Approve'}
            >
              <CheckCircle className="h-4 w-4" />
            </Button>
            <Button
              onClick={handleReject}
              disabled={updating === campaign.id}
              variant="destructive"
              size="sm"
              title={language === 'ar' ? 'رفض' : 'Reject'}
            >
              <XCircle className="h-4 w-4" />
            </Button>
          </>
        )}
        
        {campaign.status === 'approved' && (
          <>
            <Button
              onClick={handleReject}
              disabled={updating === campaign.id}
              variant="destructive"
              size="sm"
              title={language === 'ar' ? 'رفض' : 'Reject'}
            >
              <XCircle className="h-4 w-4" />
            </Button>
            <Button
              onClick={() => onUpdateStatus(campaign.id, 'pending')}
              disabled={updating === campaign.id}
              variant="outline"
              size="sm"
              title={language === 'ar' ? 'إرجاع للمراجعة' : 'Return to Pending'}
            >
              <Undo2 className="h-4 w-4" />
            </Button>
          </>
        )}
        
        {campaign.status === 'rejected' && (
          <>
            <Button
              onClick={() => onUpdateStatus(campaign.id, 'approved')}
              disabled={updating === campaign.id}
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700"
              title={language === 'ar' ? 'موافقة' : 'Approve'}
            >
              <CheckCircle className="h-4 w-4" />
            </Button>
            <Button
              onClick={() => onUpdateStatus(campaign.id, 'pending')}
              disabled={updating === campaign.id}
              variant="outline"
              size="sm"
              title={language === 'ar' ? 'إرجاع للمراجعة' : 'Return to Pending'}
            >
              <Undo2 className="h-4 w-4" />
            </Button>
          </>
        )}
      </div>

      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {language === 'ar' ? 'رفض الحملة' : 'Reject Campaign'}
            </DialogTitle>
            <DialogDescription>
              {language === 'ar' 
                ? 'يرجى تقديم سبب لرفض هذه الحملة. سيتم إرسال هذا إلى العميل.'
                : 'Please provide a reason for rejecting this campaign. This will be sent to the client.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="rejection-reason">
                {language === 'ar' ? 'سبب الرفض' : 'Rejection Reason'}
              </Label>
              <Textarea
                id="rejection-reason"
                placeholder={language === 'ar' 
                  ? 'أدخل سبب رفض الحملة...'
                  : 'Enter the reason for rejection...'}
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                rows={4}
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowRejectDialog(false);
                setRejectionReason("");
              }}
            >
              {language === 'ar' ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button
              variant="destructive"
              onClick={confirmReject}
              disabled={!rejectionReason.trim()}
            >
              {language === 'ar' ? 'تأكيد الرفض' : 'Confirm Rejection'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
